import { Component, Inject, OnInit, OnDestroy, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    Router,
} from "@angular/router";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatListModule } from "@angular/material/list";
import { MatDividerModule } from "@angular/material/divider";
import { MatRippleModule } from "@angular/material/core";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { AuthService } from "../../../core/services/auth.service";
import {
    MatDialog,
    MatDialogRef,
    MatDialogModule,
    MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import {
    CurrentSiteService,
    CurrentSite,
    ALL_SITES,
} from "../../../core/services/current-site.service";
import { SiteService } from "../../../core/services/site.service";
import { EntityContextService } from "../../../core/services/entity-context.service";
import { EntityService } from "../../../core/services/entity.service";
import { ViewUserDialogComponent } from "../users/view-user-dialog.component";
import { UserService } from "../../../core/services/user.service";
import { TourComponent } from "../../../shared/components/tour/tour.component";
import { TourService } from "../../../core/services/tour.service";
import { TourConfig } from "../../../core/models/tour-step.model";
import { ThemeService } from "../../../core/services/theme.service";
import { HttpClient } from "@angular/common/http";
import { Subject, distinctUntilChanged, takeUntil } from "rxjs";
import { CampaignWizardComponent, OnboardingStatus } from "../campaign-wizard/campaign-wizard.component";
import { environment } from "../../../../environments/environment";

@Component({
    selector: "app-switch-site-dialog",
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatListModule,
        MatIconModule,
        MatButtonModule,
        MatDividerModule,
    ],
    template: `
        <div class="switch-site-dialog">
            <h2 mat-dialog-title class="dialog-title">Switch Site</h2>
            <mat-dialog-content>
                <mat-nav-list>
                    <a
                        mat-list-item
                        (click)="selectSite(allSites)"
                        [class.selected]="selectedSite?.id === 0"
                    >
                        <mat-icon matListItemIcon>{{ selectedSite?.id === 0 ? 'check_circle' : 'public' }}</mat-icon>
                        <span>All Sites</span>
                    </a>
                    <mat-divider></mat-divider>
                    <a
                        mat-list-item
                        *ngFor="let site of sites"
                        (click)="selectSite(site)"
                        [class.selected]="site.id === selectedSite?.id"
                    >
                        <mat-icon matListItemIcon>{{
                            site.id === selectedSite?.id ? "check_circle" : "business"
                        }}</mat-icon>
                        <span>{{ site.name }}</span>
                    </a>
                </mat-nav-list>
            </mat-dialog-content>
            <mat-dialog-actions align="end">
                <button mat-stroked-button mat-dialog-close color="primary">
                    Close
                </button>
            </mat-dialog-actions>
        </div>
    `,
    styles: [
        `
            .switch-site-dialog {
                min-width: 320px;
                padding: 8px 0 0 0;
            }
            .dialog-title {
                font-size: 22px;
                font-weight: 600;
                margin-bottom: 8px;
                color: #1e3a8a;
                text-align: center;
            }
            mat-nav-list {
                padding: 0 0 8px 0;
            }
            a.mat-list-item {
                border-radius: 8px;
                margin: 4px 0;
                transition: background 0.2s, color 0.2s;
                font-size: 17px;
            }
            a.mat-list-item.selected {
                background: #e3eafe;
                color: #2a3b8f;
                font-weight: 600;
            }
            a.mat-list-item:hover {
                background: #f8fafc;
                color: #1e3a8a;
            }
            mat-icon[matListItemIcon] {
                margin-right: 12px;
                color: #1e3a8a;
            }
            mat-dialog-actions {
                margin-top: 8px;
                padding: 0 16px 8px 16px;
            }
            button[mat-stroked-button] {
                border-radius: 8px;
                font-weight: 500;
            }

            /* ── Dark mode ─────────────────────────────────────────────── */
            :host-context(.dark) .dialog-title {
                color: #e6edf3;
            }
            :host-context(.dark) mat-icon[matListItemIcon] {
                color: #8b949e;
            }
            :host-context(.dark) a.mat-list-item.selected mat-icon[matListItemIcon] {
                color: #3b82f6;
            }
            :host-context(.dark) a.mat-list-item.selected {
                background: rgba(59, 130, 246, 0.2);
                color: #93c5fd;
            }
            :host-context(.dark) a.mat-list-item:hover {
                background: rgba(255, 255, 255, 0.08);
                color: #e6edf3;
            }
        `,
    ],
    providers: [],
})
export class SwitchSiteDialogComponent {
    readonly allSites = ALL_SITES;
    sites: CurrentSite[] = [];
    selectedSite: CurrentSite | null = null;
    constructor(
        private dialogRef: MatDialogRef<SwitchSiteDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: { sites: CurrentSite[]; currentSite: CurrentSite }
    ) {
        this.sites = data.sites;
        this.selectedSite = data.currentSite;
    }
    selectSite(site: CurrentSite) {
        this.selectedSite = site;
        setTimeout(() => this.dialogRef.close(site), 120); // slight delay for visual feedback
    }
}

@Component({
    selector: "app-dashboard",
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        RouterLink,
        RouterLinkActive,
        MatSidenavModule,
        MatToolbarModule,
        MatButtonModule,
        MatIconModule,
        MatListModule,
        MatRippleModule,
        MatMenuModule,
        MatDialogModule,
        MatTooltipModule,
        TourComponent,
    ],
    template: `
        <div class="dashboard-container" data-tour-id="dashboard">
            <mat-sidenav-container class="sidenav-container">
                <mat-sidenav
                    #drawer
                    class="sidenav"
                    data-tour-id="sidenav"
                    fixedInViewport
                    [mode]="getSidenavMode()"
                    [opened]="sidenavOpen"
                >
                    <div class="sidenav-header">
                        <div class="logo">
                            <img
                                src="assets/images/lens-blueprint-logo.svg"
                                alt="Lens Blueprint Logo"
                            />
                        </div>
                    </div>
                    @if (!authService.isAdmin()) {
                        <div class="campaign-btn-wrap">
                            <button mat-raised-button color="primary" class="campaign-btn"
                                [attr.data-tour-id]="'nav-campaign-wizard'"
                                (click)="openCampaignWizard()">
                                <mat-icon>add_circle</mat-icon>
                                <span>Setup Wizard</span>
                            </button>
                        </div>
                    }
                    <div class="role-badge-wrap">
                        <span class="role-badge" [class.super-admin]="showSuperAdminBadge">
                            <mat-icon class="role-badge-icon">{{ showSuperAdminBadge ? 'shield' : 'manage_accounts' }}</mat-icon>
                            {{ authService.getRoleBadgeLabel() }}
                        </span>
                    </div>
                    <mat-nav-list class="nav-list">
                        @if (!authService.isAdmin()) {
                            @for (link of mainNavLinks; track link.route) {
                                <a
                                    mat-list-item
                                    [routerLink]="link.route"
                                    routerLinkActive="active"
                                    class="nav-item"
                                    [attr.data-tour-id]="link.tourId"
                                    (click)="closeSidenavOnMobile()"
                                >
                                    <mat-icon matListItemIcon>{{ link.icon }}</mat-icon>
                                    <span matListItemTitle>{{ link.label }}</span>
                                </a>
                            }
                        }

                        @if (showPendingInvites) {
                            <a mat-list-item
                               routerLink="/dashboard/pending-invites"
                               routerLinkActive="active"
                               class="nav-item"
                               data-tour-id="nav-pending-invites"
                               (click)="closeSidenavOnMobile()">
                                <mat-icon matListItemIcon>mark_email_unread</mat-icon>
                                <span matListItemTitle>Pending Invites</span>
                            </a>
                        }

                        @if (showAdminSection) {
                            <div class="nav-section-header">
                                <span>Administration</span>
                            </div>
                            @for (link of adminNavLinks; track link.route) {
                                <a
                                    mat-list-item
                                    [routerLink]="link.route"
                                    routerLinkActive="active"
                                    class="nav-item"
                                    [attr.data-tour-id]="link.tourId"
                                    (click)="closeSidenavOnMobile()"
                                >
                                    <mat-icon matListItemIcon>{{ link.icon }}</mat-icon>
                                    <span matListItemTitle>{{ link.label }}</span>
                                </a>
                            }
                        }

                        @if (showSuperAdminSection) {
                            <div class="nav-section-header">
                                <span>Super Admin</span>
                            </div>
                            @for (link of superAdminNavLinks; track link.route) {
                                <a
                                    mat-list-item
                                    [routerLink]="link.route"
                                    routerLinkActive="active"
                                    class="nav-item"
                                    [attr.data-tour-id]="link.tourId"
                                    (click)="closeSidenavOnMobile()"
                                >
                                    <mat-icon matListItemIcon>{{ link.icon }}</mat-icon>
                                    <span matListItemTitle>{{ link.label }}</span>
                                </a>
                            }
                        }
                    </mat-nav-list>
                    <div class="sidenav-footer">
                        <button mat-button class="logout-button" (click)="logout()" data-tour-id="sidenav-logout">
                            <mat-icon>logout</mat-icon>
                            <span>Logout</span>
                        </button>
                    </div>
                </mat-sidenav>
                <mat-sidenav-content class="content">
                    <mat-toolbar class="toolbar">
                        <div class="toolbar-content">
                            <div class="toolbar-left">
                                <button
                                    mat-icon-button
                                    (click)="toggleSidenav()"
                                    class="menu-button"
                                    aria-label="Toggle menu"
                                    matTooltip="Toggle navigation menu"
                                >
                                    <mat-icon>menu</mat-icon>
                                </button>
                                <h2>{{ getCurrentPageTitle() }}</h2>
                            </div>
                            <div class="user-info">
                                <button
                                    mat-stroked-button
                                    color="primary"
                                    data-tour-id="toolbar-switch-site"
                                    (click)="switchSite()"
                                    style="margin-right: 16px;"
                                >
                                    Switch Site ({{ currentSite.name || 'No Site' }})
                                </button>
                                <button
                                    mat-icon-button
                                    data-tour-id="toolbar-dark-mode"
                                    (click)="themeService.toggle()"
                                    [attr.aria-label]="themeService.isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
                                    [matTooltip]="themeService.isDark() ? 'Switch to light mode' : 'Switch to dark mode'"
                                >
                                    <mat-icon>{{ themeService.isDark() ? 'light_mode' : 'dark_mode' }}</mat-icon>
                                </button>
                                <button mat-icon-button [matMenuTriggerFor]="userMenu" data-tour-id="toolbar-profile-menu" aria-label="Profile menu" matTooltip="Profile menu">
                                    <mat-icon>account_circle</mat-icon>
                                </button>
                                <mat-menu #userMenu="matMenu">
                                    <button mat-menu-item (click)="viewProfile()">
                                        <mat-icon>person</mat-icon>
                                        <span>View Profile</span>
                                    </button>
                                    @if (!authService.isAdmin()) {
                                    <button mat-menu-item (click)="restartTour()">
                                        <mat-icon>help_outline</mat-icon>
                                        <span>Restart Tour</span>
                                    </button>
                                    }
                                </mat-menu>
                            </div>
                        </div>
                    </mat-toolbar>
                    <div class="page-content" data-tour-id="page-content">
                        <router-outlet></router-outlet>
                    </div>
                </mat-sidenav-content>
            </mat-sidenav-container>
            <app-tour></app-tour>
        </div>
    `,
    styles: [
        `
            .dashboard-container {
                height: 100vh;
                background: var(--background-color);
            }
            .sidenav-container {
                height: 100%;
            }
            .sidenav {
                width: 280px;
                background: linear-gradient(
                    180deg,
                    #1e3a8a 0%,
                    #2563eb 100%
                );
                border-right: none;
                box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);
            }
            :host-context(html.dark) .sidenav {
                background: linear-gradient(
                    180deg,
                    #161b2e 0%,
                    #1e2843 100%
                );
            }
            .sidenav-header {
                padding: 24px;
                background: rgba(255, 255, 255, 0.1);
                border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            }
            .logo {
                display: flex;
                align-items: center;
                gap: 12px;
                color: white;
            }
            .logo img {
                width: 180px;
                height: auto;
                max-height: 60px;
            }
            .logo h1 {
                font-size: 24px;
                font-weight: 600;
                margin: 0;
            }
            .nav-list {
                padding: 16px 0;
            }
            .nav-list .mat-mdc-list-item {
                color: white;
            }
            .nav-list .mat-mdc-list-item .mdc-list-item__primary-text {
                color: white;
            }
            .nav-list .mat-mdc-list-item-title {
                color: white;
            }
            .nav-list .mat-mdc-list-item-content {
                color: white;
            }
            .nav-item {
                margin: 8px 16px;
                border-radius: 8px;
                color: white;
                transition: all 0.3s ease;
            }
            .nav-item:hover {
                background: rgba(255, 255, 255, 0.1);
                color: white;
            }
            .nav-item.active {
                background: rgba(255, 255, 255, 0.2);
                color: white;
            }
            .nav-item mat-icon {
                color: white;
            }
            .nav-item .mdc-list-item__primary-text {
                color: white;
            }
            .nav-item .mat-mdc-list-item-title {
                color: white;
            }
            .role-badge-wrap {
                padding: 8px 16px 4px;
            }
            .role-badge {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 4px 12px 4px 8px;
                border-radius: 20px;
                background: rgba(255, 255, 255, 0.15);
                color: rgba(255, 255, 255, 0.9);
                font-size: 12px;
                font-weight: 600;
                letter-spacing: 0.04em;
                text-transform: uppercase;
            }
            .role-badge.super-admin {
                background: linear-gradient(90deg, rgba(251,191,36,0.25), rgba(245,158,11,0.35));
                color: #fde68a;
                border: 1px solid rgba(251,191,36,0.4);
            }
            .role-badge-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }
            .nav-section-header {
                padding: 12px 24px 4px;
                font-size: 10px;
                font-weight: 700;
                letter-spacing: 0.1em;
                text-transform: uppercase;
                color: rgba(255, 255, 255, 0.45);
            }
            .campaign-btn-wrap {
                padding: 8px 16px 12px;
            }
            .campaign-btn {
                width: 100%;
                display: flex;
                align-items: center;
                gap: 8px;
                background: rgba(255, 255, 255, 0.15) !important;
                color: white !important;
                border-radius: 8px;
                font-weight: 600;
                letter-spacing: 0.02em;
                transition: background 0.2s;
            }
            .campaign-btn:hover {
                background: rgba(255, 255, 255, 0.25) !important;
            }
            .campaign-btn mat-icon {
                color: white;
            }
            .sidenav-footer {
                padding: 16px;
                border-top: 1px solid rgba(255, 255, 255, 0.1);
                position: absolute;
                bottom: 0;
                width: 100%;
            }
            .logout-button {
                width: 100%;
                color: white;
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 12px;
                border-radius: 8px;
                transition: all 0.3s ease;
            }
            .logout-button:hover {
                background: rgba(255, 255, 255, 0.1);
                color: white;
            }
            .logout-button mat-icon {
                color: white;
            }
            .logout-button span {
                color: white;
            }
            .content {
                background: var(--background-color);
            }
            .toolbar {
                background: var(--card-background);
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                position: sticky;
                top: 0;
                z-index: 1000;
            }
            .toolbar-content {
                width: 100%;
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0 24px;
            }
            .toolbar-left {
                display: flex;
                align-items: center;
                gap: 16px;
            }
            .menu-button {
                color: var(--primary-color);
            }
            .toolbar-content h2 {
                margin: 0;
                font-size: 20px;
                font-weight: 500;
                color: var(--text-primary);
            }
            .user-info {
                display: flex;
                align-items: center;
                gap: 12px;
            }
            .user-info button {
                color: var(--primary-color);
            }
            .page-content {
                padding: 24px;
                height: calc(100vh - 64px);
                overflow-y: auto;
            }
            @media (max-width: 768px) {
                .sidenav {
                    width: 240px;
                }
                .toolbar-content {
                    padding: 0 16px;
                }
                .toolbar-content h2 {
                    font-size: 18px;
                }
                .user-name {
                    display: none;
                }
                .page-content {
                    padding: 16px;
                }
            }
        `,
    ],
})
export class DashboardComponent implements OnInit, OnDestroy {
    protected readonly themeService = inject(ThemeService);
    sidenavOpen = false;

    private readonly destroy$ = new Subject<void>();
    private tourBecameActive = false;
    private isTourActiveNow = false;
    private pendingOnboardingWizardStatus: OnboardingStatus | null = null;

    mainNavLinks = [
        { label: "Entity",   icon: "business",       route: "/dashboard/entity",   tourId: "nav-entity" },
        { label: "Sites",    icon: "location_city",  route: "/dashboard/sites",    tourId: "nav-sites" },
        { label: "Groups",   icon: "group",          route: "/dashboard/groups",   tourId: "nav-groups" },
        { label: "Users",    icon: "people",         route: "/dashboard/users",    tourId: "nav-users" },
        { label: "Scores",   icon: "assessment",     route: "/dashboard/scores",   tourId: "nav-scores" },
    ];

    adminNavLinks = [
        { label: "All Entities", icon: "corporate_fare", route: "/dashboard/admin/entities",  tourId: "nav-admin-entities" },
        { label: "Invite Admin", icon: "person_add",     route: "/dashboard/admin/invite",    tourId: "nav-admin-invite"    },
    ];

    superAdminNavLinks = [
        { label: "Audit Logs", icon: "receipt_long", route: "/dashboard/admin/logs",    tourId: "nav-admin-logs" },
        { label: "App Config", icon: "settings",     route: "/dashboard/admin/config",  tourId: "nav-admin-config" },
    ];

    get showPendingInvites(): boolean {
        return this.authService.canSeePendingInvites();
    }

    get showAdminSection(): boolean {
        return this.authService.isAdmin();
    }

    get showSuperAdminSection(): boolean {
        return this.authService.isSuperAdmin();
    }

    get showSuperAdminBadge(): boolean {
        return this.authService.isSuperAdmin();
    }

    // Dynamically load sites from the API
    sites: CurrentSite[] = [];

    // create a dummy site
    currentSite: CurrentSite = { id: 1, name: "No Site" };

    constructor(
        private router: Router,
        public authService: AuthService,
        private dialog: MatDialog,
        private http: HttpClient,
        private currentSiteService: CurrentSiteService,
        private siteService: SiteService,
        private entityContext: EntityContextService,
        private entityService: EntityService,
        private userService: UserService,
        private tourService: TourService
    ) {
        this.currentSite = this.currentSiteService.getSite();
        this.siteService.getSites().subscribe({
            next: (sites) => { this.sites = sites; },
            error: () => { /* super_admin may get 403 until backend is redeployed */ },
        });
        this.checkScreenSize();
        window.addEventListener("resize", () => this.checkScreenSize());
    }

    ngOnInit(): void {
        if (this.authService.requiresEntityCreation() && !this.authService.isAdmin()) {
            this.router.navigate(["/auth/create-entity"]);
            return;
        }

        this.setupDeferredOnboardingWizardOpen();

        // Set entity context if user has entity
        const currentUser = this.authService.getCurrentUser();
        if (currentUser?.entity_id && !this.entityContext.hasEntity()) {
            this.entityService.getEntityById(currentUser.entity_id).subscribe({
                next: (entity) => {
                    this.entityContext.setEntity(entity);
                },
                error: (error) => {
                    console.error('Error loading entity:', error);
                }
            });
        }

        this.siteService.getSites().subscribe({
            next: (sites) => {
                this.sites = sites;
                if (sites && sites.length > 0) {
                    const persisted = this.currentSiteService.getSite();
                    // Restore persisted selection if it still exists, or use All Sites (id=0), or fall back to first site
                    const stillValid = persisted.id === 0 || sites.some(s => s.id === persisted.id);
                    if (stillValid && persisted.id !== 1) {
                        this.currentSite = persisted;
                        this.currentSiteService.setSite(this.currentSite);
                    } else {
                        this.currentSite = this.sites[0];
                        this.currentSiteService.setSite(this.currentSite);
                    }
                } else {
                    this.currentSite = { id: 0, name: "No Site Selected" };
                    this.currentSiteService.setSite(this.currentSite);
                }
            },
            error: (error) => {
                console.error('Error loading sites:', error);
                this.currentSite = { id: 0, name: "No Site Selected" };
                this.currentSiteService.setSite(this.currentSite);
            }
        });

        if (!this.authService.isAdmin()) {
            // Check onboarding status and auto-open wizard for users who haven't finished setup
            this.http.get<OnboardingStatus>(`${environment.apiUrl}/api/v1/entity/onboarding-status`)
                .subscribe({
                    next: (status) => {
                        if (!status.has_entity) {
                            this.router.navigate(["/auth/create-entity"]);
                            return;
                        }

                        if (!status.has_site) {
                            if (this.tourService.isTourCompleted("main-app-tour")) {
                                setTimeout(() => this.openCampaignWizard(true, status), 600);
                                return;
                            }

                            this.pendingOnboardingWizardStatus = status;
                            this.tryOpenPendingCampaignWizard();
                        }
                    },
                    error: () => {
                        /* non-critical — wizard can still be opened manually */
                    },
                });

            // Check if user is new and start tour
            this.checkAndStartTour();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupDeferredOnboardingWizardOpen(): void {
        this.tourService.isActive
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((isActive) => {
                this.isTourActiveNow = isActive;
                if (isActive) {
                    this.tourBecameActive = true;
                    return;
                }

                if (this.tourBecameActive) {
                    this.tryOpenPendingCampaignWizard();
                }
            });
    }

    private tryOpenPendingCampaignWizard(): void {
        if (!this.pendingOnboardingWizardStatus) {
            return;
        }

        if (!this.tourBecameActive || this.isTourActiveNow) {
            return;
        }

        const status = this.pendingOnboardingWizardStatus;
        this.pendingOnboardingWizardStatus = null;
        setTimeout(() => this.openCampaignWizard(true, status), 350);
    }

    private checkAndStartTour(): void {
        // Show tour whenever the user has not completed it yet (new profile or first visit)
        setTimeout(() => {
            if (this.tourService.isTourCompleted("main-app-tour")) {
                return;
            }
            this.initializeTour();
        }, 1000); // Wait for dashboard and child route to load
    }

    private initializeTour(): void {
        const tourConfig: TourConfig = {
            id: 'main-app-tour',
            name: 'Welcome Tour',
            autoStart: true,
            skipIfCompleted: true,
            steps: [
                // ── Setup Wizard ──────────────────────────────────────────
                {
                    id: 'setup-wizard',
                    title: 'Setup Wizard',
                    text: 'New here? Click "Setup Wizard" to create your site, group, and add team members in one guided flow. Returning users can use it to set up additional sites and groups.',
                    selector: '[data-tour-id="nav-campaign-wizard"]',
                    position: 'right',
                    route: '/dashboard/entity',
                    showProgress: true
                },
                // ── Entity ────────────────────────────────────────────────
                // ── Toolbar ───────────────────────────────────────────────
                {
                    id: 'switch-site',
                    title: 'Switch Site',
                    text: 'Working across multiple locations? Click here to switch the active site. Data in Groups, Users, and Scores updates to reflect the selected site.',
                    selector: '[data-tour-id="toolbar-switch-site"]',
                    position: 'bottom',
                    route: '/dashboard/entity',
                    waitForElement: true,
                    waitTimeout: 3000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'dark-mode',
                    title: 'Dark Mode',
                    text: 'Prefer a darker interface? Toggle between light and dark mode here. Your preference is saved automatically.',
                    selector: '[data-tour-id="toolbar-dark-mode"]',
                    position: 'bottom',
                    route: '/dashboard/entity',
                    waitForElement: true,
                    waitTimeout: 3000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'profile-section',
                    title: 'Your Profile',
                    text: 'Click here to open your profile menu. You can view and update your personal details, or restart this tour at any time.',
                    selector: '[data-tour-id="toolbar-profile-menu"]',
                    position: 'bottom',
                    route: '/dashboard/entity',
                    waitForElement: true,
                    waitTimeout: 3000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'entity-section',
                    title: 'Entity (Your Organisation)',
                    text: 'This is your organisation\'s profile. Everything in the app — sites, groups, and users — belongs to this entity.',
                    selector: '[data-tour-id="nav-entity"]',
                    position: 'right',
                    route: '/dashboard/entity',
                    showProgress: true
                },
                {
                    id: 'edit-entity',
                    title: 'Edit Entity Details',
                    text: 'Click "Edit Entity" to update your organisation\'s name, registration number, address, and other details.',
                    selector: '[data-tour-id="entity-edit-btn"]',
                    position: 'bottom',
                    route: '/dashboard/entity',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                // ── Sites ─────────────────────────────────────────────────
                {
                    id: 'sites-section',
                    title: 'Sites',
                    text: 'Sites represent your physical locations or departments. You need at least one site before you can create groups or add users.',
                    selector: '[data-tour-id="nav-sites"]',
                    position: 'right',
                    route: '/dashboard/sites',
                    showProgress: true
                },
                {
                    id: 'create-site',
                    title: 'Add a Site',
                    text: 'Click "Add Site" to create a new location. Each site can have its own groups and users, making it easy to manage multiple branches.',
                    selector: '[data-tour-id="sites-add-btn"]',
                    position: 'bottom',
                    route: '/dashboard/sites',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                // ── Groups ────────────────────────────────────────────────
                {
                    id: 'groups-section',
                    title: 'Groups',
                    text: 'Groups organise users into teams. Each group can have its own assessment results and a generated team report.',
                    selector: '[data-tour-id="nav-groups"]',
                    position: 'right',
                    route: '/dashboard/groups',
                    showProgress: true
                },
                {
                    id: 'create-group',
                    title: 'Add a Group',
                    text: 'Click "Add Group" to create a new team. You can optionally enable AI interpretation, which generates a written summary alongside the group\'s scores.',
                    selector: '[data-tour-id="groups-add-btn"]',
                    position: 'bottom',
                    route: '/dashboard/groups',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                // ── Users ─────────────────────────────────────────────────
                {
                    id: 'users-section',
                    title: 'Users',
                    text: 'Manage all your candidates and team members here. You can add users, track their assessment status, assign them to groups, and download their reports.',
                    selector: '[data-tour-id="nav-users"]',
                    position: 'right',
                    route: '/dashboard/users',
                    showProgress: true
                },
                {
                    id: 'invite-user',
                    title: 'Invite a Manager',
                    text: 'Need help managing the platform? Click "Invite Managers" to send an email invitation to an HR Manager or HR Personnel so they can log in and assist.',
                    selector: '[data-tour-id="users-invite-btn"]',
                    position: 'bottom',
                    route: '/dashboard/users',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'add-user',
                    title: 'Add a User',
                    text: 'Click "Add Candidate" to register a candidate manually. You\'ll assign them a site, group, and contact details so they can be invited to take the assessment.',
                    selector: '[data-tour-id="users-add-btn"]',
                    position: 'bottom',
                    route: '/dashboard/users',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'bulk-register',
                    title: 'Bulk Registration',
                    text: 'Adding many users at once? Click "Bulk Register" to upload a CSV file. The app validates each row and shows you a preview before creating the accounts.',
                    selector: '[data-tour-id="users-bulk-register-btn"]',
                    position: 'bottom',
                    route: '/dashboard/users',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'export-users',
                    title: 'Export User List',
                    text: 'Click "Export" to download your full user list as a CSV, Excel, or PDF file — useful for record-keeping or sharing with stakeholders.',
                    selector: '[data-tour-id="users-export-btn"]',
                    position: 'bottom',
                    route: '/dashboard/users',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'bulk-report',
                    title: 'Download Assessment Reports',
                    text: 'Select users from the table and click "Download Selected" to get their PDF reports, or use "Download All Reports" to batch-download every completed assessment at once.',
                    selector: '[data-tour-id="users-bulk-report-actions"]',
                    position: 'top',
                    route: '/dashboard/users',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                // ── Scores ────────────────────────────────────────────────
                {
                    id: 'scores-section',
                    title: 'Scores & Analytics',
                    text: 'View assessment scores and analytics for any group. Switch between an overview, a detailed score table, and performance charts — all in one place.',
                    selector: '[data-tour-id="nav-scores"]',
                    position: 'right',
                    route: '/dashboard/scores',
                    showProgress: true
                },
                {
                    id: 'scores-group-selector',
                    title: 'Select a Group to Analyse',
                    text: 'Choose a group from this dropdown to load their results. The overview cards, score table, and charts all update based on the group you select.',
                    selector: '[data-tour-id="scores-group-selector"]',
                    position: 'bottom',
                    route: '/dashboard/scores',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'scores-tab-overview',
                    title: 'Overview Tab',
                    text: 'The Overview tab shows high-level performance metrics and a radar chart comparing each competency across the group — great for a quick snapshot of team strengths.',
                    selector: '[data-tour-id="scores-tab-overview"]',
                    position: 'bottom',
                    route: '/dashboard/scores',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'scores-tab-detailed',
                    title: 'Detailed Scores Tab',
                    text: 'Detailed Scores shows each individual\'s scores in a sortable table. You can also export the data as CSV, Excel, or PDF directly from this tab.',
                    selector: '[data-tour-id="scores-tab-detailed"]',
                    position: 'bottom',
                    route: '/dashboard/scores',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'scores-tab-analytics',
                    title: 'Analytics Tab',
                    text: 'The Analytics tab breaks down performance with charts — a distribution of score ranges across the group and a leaderboard of top performers.',
                    selector: '[data-tour-id="scores-tab-analytics"]',
                    position: 'bottom',
                    route: '/dashboard/scores',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                // ── Pending Invites ───────────────────────────────────────
                {
                    id: 'pending-invites-section',
                    title: 'Pending Invites',
                    text: 'Track HR Manager and HR Personnel invitations you\'ve sent here. You can see who has accepted, who is still pending, and resend invites if needed.',
                    selector: '[data-tour-id="nav-pending-invites"]',
                    position: 'right',
                    route: '/dashboard/pending-invites',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'pending-invites-resend',
                    title: 'Resend Invitations',
                    text: 'Select one or more pending invites from the table, then click "Resend Selected" to send them a fresh invitation email.',
                    selector: '[data-tour-id="pending-invites-resend-btn"]',
                    position: 'bottom',
                    route: '/dashboard/pending-invites',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'pending-invites-cancel',
                    title: 'Cancel Invitations',
                    text: 'Select invites and click "Cancel Selected" to revoke them. Cancelled invites can no longer be used to register.',
                    selector: '[data-tour-id="pending-invites-cancel-btn"]',
                    position: 'bottom',
                    route: '/dashboard/pending-invites',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                {
                    id: 'pending-invites-export',
                    title: 'Export Invites',
                    text: 'Download the full invite list as a CSV or PDF — useful for record-keeping or sharing invite status with your team.',
                    selector: '[data-tour-id="pending-invites-export-btn"]',
                    position: 'bottom',
                    route: '/dashboard/pending-invites',
                    waitForElement: true,
                    waitTimeout: 5000,
                    skipIfNotFound: true,
                    showProgress: true
                },
                // ── Wrap-up ───────────────────────────────────────────────
                {
                    id: 'tour-complete',
                    title: 'You\'re All Set!',
                    text: 'That\'s the full tour! Start by running the Setup Wizard, then explore each section at your own pace. You can restart this tour anytime from your profile menu.',
                    selector: '[data-tour-id="sidenav-logout"]',
                    position: 'right',
                    route: '/dashboard/entity',
                    showProgress: true
                }
            ]
        };
        
        this.tourService.startTour(tourConfig).catch(err => {
            console.error('Error starting tour:', err);
        });
    }

    restartTour(): void {
        this.tourService.resetTour('main-app-tour');
        this.initializeTour();
    }

    private checkScreenSize() {
        if (window.innerWidth > 768) {
            this.sidenavOpen = true;
        } else {
            this.sidenavOpen = false;
        }
    }

    getSidenavMode(): "side" | "over" {
        return window.innerWidth > 768 ? "side" : "over";
    }

    getCurrentPageTitle(): string {
        const url = this.router.url;
        const pathWithoutQuery = url.split("?")[0]; // Remove query parameters
        const pathSegments = pathWithoutQuery.split("/");

        // Handle specific route patterns
        if (pathSegments.includes("sites") && pathSegments.length === 2) {
            return "Sites";
        }
        if (pathSegments.includes("entity")) {
            return "Entity Details";
        }
        if (pathSegments.includes("groups") && pathSegments.includes("report")) {
            return "Group Report";
        }
        if (pathSegments.includes("users") && pathSegments.includes("report")) {
            return "User Report";
        }

        // Get the main section (sites, groups, users, scores)
        if (pathSegments.includes("pending-invites")) return "Pending Invites";
        if (pathSegments.includes("logs"))            return "Audit Logs";
        if (pathSegments.includes("config"))          return "App Config";
        if (pathSegments.includes("entity") && pathSegments.includes("admin")) return "Entity Detail";
        if (pathSegments.includes("entities"))        return "All Entities";

        const mainSection = pathSegments.find((segment) =>
            ["entity", "sites", "groups", "users", "scores"].includes(segment)
        );

        if (mainSection) {
            return mainSection.charAt(0).toUpperCase() + mainSection.slice(1);
        }

        return "Dashboard";
    }

    toggleSidenav() {
        this.sidenavOpen = !this.sidenavOpen;
    }

    closeSidenavOnMobile() {
        // Close sidenav on mobile when navigation item is clicked
        if (window.innerWidth <= 768) {
            this.sidenavOpen = false;
        }
    }

    logout() {
        this.authService.logout();
        this.router.navigate(["/login"]);
    }

    openCampaignWizard(isFirstTimeUser = false, onboardingStatus?: OnboardingStatus): void {
        this.dialog.open(CampaignWizardComponent, {
            width: "860px",
            maxWidth: "95vw",
            maxHeight: "90vh",
            disableClose: isFirstTimeUser,
            data: { isFirstTimeUser, onboardingStatus },
        }).afterClosed().subscribe((result: { navigateTo: string } | null) => {
            if (result?.navigateTo) {
                this.router.navigate([result.navigateTo]);
            }
        });
    }

    switchSite() {
        this.siteService.getSites().subscribe((sites) => {
            this.sites = sites;
            const dialogRef = this.dialog.open(SwitchSiteDialogComponent, {
                width: "320px",
                data: { currentSite: this.currentSite || null, sites: this.sites },
            });
            dialogRef.afterClosed().subscribe((site: CurrentSite) => {
                if (site) {
                    this.currentSite = site;
                    this.currentSiteService.setSite(site);
                }
            });
        });
    }

    viewProfile() {
        const currentUser = this.authService.getCurrentUser();
        if (currentUser?.id) {
            // Fetch full user data from the API
            this.userService.getUserById(currentUser.id).subscribe({
                next: (user) => {
                    this.dialog.open(ViewUserDialogComponent, {
                        width: "500px",
                        data: { user },
                    });
                },
                error: (error) => {
                    console.error('Error loading user profile:', error);
                    // Fallback to using the current user data if API call fails
                    this.dialog.open(ViewUserDialogComponent, {
                        width: "500px",
                        data: { user: currentUser },
                    });
                }
            });
        }
    }
}
